/**
 * What a refused publish becomes on screen — Story 25.4.
 *
 * `POST …/publish` refuses with the scan's findings in `details.scan` (Story
 * 25.3), and the dialog BRANCHES ON THE CODE, never on the message or the plan:
 *
 *   publish_held           → held for review     (Free and Starter, a field intent)
 *   publish_blocked        → can't publish here  (Free and Starter, a regulated vertical)
 *   attestation_required   → confirm to publish  (Pro and above, a regulated vertical)
 *
 * THE ATTESTATION CONTROL APPEARS ONLY WHERE THE ROUTE WOULD ACCEPT ONE, and
 * that is decided by the server, not here: only a plan that publishes first
 * answers `attestation_required` (`services/moderation`), and on Free and
 * Starter the same funnel answers `publish_blocked`, where an attestation lifts
 * nothing. `packages/api/test/moderation-customer.test.ts` holds the plans to
 * the codes; `tests/publish-copy.test.ts` holds the codes to the stages.
 *
 * Anything else is not an outcome: the dialog stays on its confirmation with the
 * API's own message, as it always has.
 *
 * NO RUNTIME IMPORTS BUT `publish-copy.ts`, which has none, so a node-side test
 * loads both without resolving the workspace.
 */
import { COMPOSED, DRAWN, type Intent, type Vertical } from './publish-copy'

export type Evidence = { where: string; text: string }

export type Outcome =
  | { kind: 'held'; intents: Intent[]; evidence: Evidence[] }
  | { kind: 'blocked'; verticals: Vertical[]; evidence: Evidence[] }
  | { kind: 'attest'; verticals: Vertical[]; evidence: Evidence[] }

const INTENTS: readonly string[] = ['password', 'payment_card', 'government_id']
const VERTICALS: readonly string[] = ['employment', 'credit', 'insurance', 'tenancy']

type Finding = {
  kind?: unknown
  intent?: unknown
  vertical?: unknown
  at?: unknown
  step?: { number?: unknown; title?: unknown }
  text?: unknown
}

const namesIn = <T extends string>(value: unknown, known: readonly string[]): T[] =>
  Array.isArray(value) ? (value.filter((v) => typeof v === 'string' && known.includes(v)) as T[]) : []

/** "Step 4 · Contact", or where a finding with no step was read. */
function whereOf(finding: Finding): string {
  const step = finding.step
  if (step && typeof step.number === 'number') {
    return typeof step.title === 'string' && step.title ? DRAWN.step(step.number, step.title) : `Step ${step.number}`
  }
  return (typeof finding.at === 'string' && COMPOSED.where[finding.at]) || COMPOSED.elsewhere
}

/** One row per place, however many phrases matched there. */
function evidenceOf(findings: readonly Finding[]): Evidence[] {
  const rows: Evidence[] = []
  const seen = new Set<string>()
  for (const finding of findings) {
    if (typeof finding.text !== 'string') continue
    const row = { where: whereOf(finding), text: finding.text }
    const key = `${row.where}\n${row.text}`
    if (seen.has(key)) continue
    seen.add(key)
    rows.push(row)
  }
  return rows
}

export function outcomeOf(failure: { code: string; details?: Record<string, unknown> | undefined }): Outcome | null {
  const scan = (failure.details?.['scan'] ?? {}) as { findings?: unknown; field_intents?: unknown; verticals?: unknown }
  const findings = (Array.isArray(scan.findings) ? scan.findings : []) as Finding[]

  if (failure.code === 'publish_held') {
    return {
      kind: 'held',
      intents: namesIn<Intent>(scan.field_intents, INTENTS),
      evidence: evidenceOf(findings.filter((f) => f.kind === 'field_intent')),
    }
  }
  if (failure.code === 'publish_blocked' || failure.code === 'attestation_required') {
    // The attestation lists what is still to confirm; a block lists what was found.
    const verticals = namesIn<Vertical>(
      failure.code === 'attestation_required' ? failure.details?.['attest'] : scan.verticals,
      VERTICALS,
    )
    const evidence = evidenceOf(
      findings.filter(
        (f) => f.kind === 'regulated_vertical' && typeof f.vertical === 'string' && (verticals as string[]).includes(f.vertical),
      ),
    )
    return failure.code === 'attestation_required'
      ? { kind: 'attest', verticals, evidence }
      : { kind: 'blocked', verticals, evidence }
  }
  return null
}
